// Tek etiket helper'ı + tek sıralama anahtarı (spec K8). Yıllıkta çıktı "2025" → mevcut görünüm değişmez (K6).
import { PERIOD_LABELS, PERIOD_TYPES, type PeriodType } from "../schemas/period.js";

const SHORT: Record<PeriodType, string> = { annual: "", q1: "Q1", q2: "Q2", q3: "Q3" };
const ORDER: Record<PeriodType, number> = { q1: 1, q2: 2, q3: 3, annual: 9 };

export function formatReportingPeriod(
  year: number,
  type: PeriodType,
  opts?: { short?: boolean },
): string {
  if (type === "annual") return year.toString();
  return opts?.short
    ? `${year.toString()} ${SHORT[type]}`
    : `${year.toString()} · ${PERIOD_LABELS[type]}`;
}
export function periodSortKey(year: number, type: PeriodType): number {
  return year * 10 + ORDER[type];
}
export function previousPeriod(year: number, type: PeriodType): { year: number; type: PeriodType } {
  return { year: year - 1, type };
}
// applicablePeriods yoksa (eski snapshot/haritada olmayan id) her dönem geçerli.
export function isPeriodApplicable(
  periods: readonly PeriodType[] | undefined | null,
  type: PeriodType,
): boolean {
  return !periods || periods.includes(type);
}
// Tavan + kesişim (eng review C5): bölüm tavan, soru tercih; undefined = kısıt yok.
export function effectivePeriods(
  sectionPeriods: readonly PeriodType[] | undefined,
  questionPeriods: readonly PeriodType[] | undefined,
): PeriodType[] {
  return PERIOD_TYPES.filter(
    (p) => isPeriodApplicable(sectionPeriods, p) && isPeriodApplicable(questionPeriods, p),
  );
}

// Dönemin takvim tarih aralığı + insan-okur etiket (design review 1C/3A). qN kümülatif ara
// dönem: HER ZAMAN 1 Ocak'ta başlar (3/6/9 aylık), annual tüm yıl. Etiket sadece tarih
// aralığıdır — ekstra kelime yok; en dash (–) boşluklu, ay adları Türkçe.
export const MONTH_NAMES_TR = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
] as const;
// [ay(1-indexli son gün), yıl-sonu-ayı-indeks(0-indexli, MONTH_NAMES_TR için)]
const PERIOD_END: Record<PeriodType, { month: number; day: number }> = {
  annual: { month: 12, day: 31 },
  q1: { month: 3, day: 31 },
  q2: { month: 6, day: 30 },
  q3: { month: 9, day: 30 },
};

export function periodDateRange(
  year: number,
  type: PeriodType,
): { start: string; end: string; label: string } {
  const pad2 = (n: number): string => n.toString().padStart(2, "0");
  const { month, day } = PERIOD_END[type];
  const start = `${year.toString()}-01-01`;
  const end = `${year.toString()}-${pad2(month)}-${pad2(day)}`;
  const label = `1 Ocak – ${day.toString()} ${MONTH_NAMES_TR[month - 1]} ${year.toString()}`;
  return { start, end, label };
}

// M17 T5 — mali yıl (clients.fiscal_year_start_month). "MY{N}" = takvim yılı N'de BİTEN mali yıl
// (Temmuz başlangıcı: MY2026 = 1 Tem 2025 – 30 Haz 2026). Çeyrek kümülatif kalır (K1): qN mali yıl
// başından 3/6/9 ay — ay sayısı periodDateRange'in takvim bitişinden okunur (kopya tablo yok).
export type FiscalPeriodKind = "calendar_year" | "fiscal_year" | "quarter";

/** Tek etiket yeri (TASTE: "MY"; FY'ye geçiş yalnız burada). startMonth=1 → takvim kısa formu. */
export function periodLabel(year: number, type: PeriodType, startMonth: number): string {
  if (startMonth === 1) return formatReportingPeriod(year, type, { short: true });
  const base = `MY${year.toString()}`;
  return type === "annual" ? base : `${base} ${SHORT[type]}`;
}

export function fiscalPeriodRange(
  year: number,
  startMonth: number,
  type: PeriodType,
): { start: string; end: string; label: string; kind: FiscalPeriodKind } {
  const cal = periodDateRange(year, type);
  const kind: FiscalPeriodKind =
    type !== "annual" ? "quarter" : startMonth === 1 ? "calendar_year" : "fiscal_year";
  if (startMonth === 1) {
    return { start: cal.start, end: cal.end, label: formatReportingPeriod(year, type), kind };
  }
  const pad2 = (n: number): string => n.toString().padStart(2, "0");
  const months = Number(cal.end.slice(5, 7)); // kümülatif ay sayısı: 3/6/9/12
  const startYear = year - 1;
  // Son ayın 0-indeksli mutlak ayı; Date.UTC(y, m + 1, 0) = o ayın son günü.
  const lastAbs = startYear * 12 + (startMonth - 1) + months - 1;
  const endYear = Math.floor(lastAbs / 12);
  const endMonth0 = lastAbs % 12;
  const endDay = new Date(Date.UTC(endYear, endMonth0 + 1, 0)).getUTCDate();
  return {
    start: `${startYear.toString()}-${pad2(startMonth)}-01`,
    end: `${endYear.toString()}-${pad2(endMonth0 + 1)}-${pad2(endDay)}`,
    label: periodLabel(year, type, startMonth),
    kind,
  };
}

/** "2025-07-01","2026-06-30" → "1 Tem 2025 – 30 Haz 2026" (kısa ay adı = ilk 3 harf). */
export function shortDateRange(start: string, end: string): string {
  const fmt = (iso: string): string => {
    const [y, m, d] = iso.split("-");
    const month = MONTH_NAMES_TR[Number(m) - 1] ?? "";
    return `${Number(d).toString()} ${month.slice(0, 3)} ${y ?? ""}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
